<template>
    <div :class="['ui-loader', { 'ui-loader--fullscreen': props.fullscreen, 'ui-loader--opaque': props.opaque }]">
        <div class="ui-loader__inner">
            <div class="ui-loader__frame" aria-hidden="true">
                <svg ref="svgEl" viewBox="0 0 100 100" fill="none">
                    <g ref="groupEl">
                        <path
                            ref="pathEl"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            :stroke-width="config.strokeWidth"
                            opacity="0.1"
                        />
                    </g>
                </svg>
            </div>

            <div v-if="hasSlotOrMessage" class="ui-loader__text">
                <p
                    :class="[
                        'ui-loader__title',
                        { 'text-white': props.fullscreen, 'text-card-500': !props.fullscreen }
                    ]"
                >
                    <slot>{{ message }}</slot>
                </p>
            </div>

            <div
                v-if="typeof progress === 'number'"
                class="ui-loader__progressbar"
                role="progressbar"
                :aria-valuenow="clampedProgress"
                aria-valuemin="0"
                aria-valuemax="100"
            >
                <div class="ui-loader__progressbar-fill" :style="{ width: clampedProgress + '%' }"></div>
                <span class="ui-loader__progressbar-label">{{ Math.round(clampedProgress) }}%</span>
            </div>

            <div v-if="steps && steps.length > 0" class="ui-loader__steps">
                <p v-if="progressSummary" class="ui-loader__progress">
                    {{ progressSummary }}
                </p>
                <ul class="ui-loader__steps-list" role="list">
                    <li
                        v-for="(step, index) in steps"
                        :key="index"
                        :class="['ui-loader__step', `ui-loader__step--${getStepState(index)}`]"
                    >
                        <span class="ui-loader__step-icon" aria-hidden="true">
                            <svg
                                v-if="getStepState(index) === 'done'"
                                viewBox="0 0 16 16"
                                fill="none"
                                width="14"
                                height="14"
                            >
                                <path
                                    d="M3 8.5 6.5 12 13 4.5"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                            <span v-else-if="getStepState(index) === 'active'" class="ui-loader__step-pulse"></span>
                            <span v-else class="ui-loader__step-dot"></span>
                        </span>
                        <span class="ui-loader__step-label">{{ step }}</span>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref, shallowRef } from 'vue';

const props = defineProps<{
    fullscreen?: boolean;
    opaque?: boolean;
    steps?: string[];
    stepIndex?: number;
    message?: string;
    progress?: number | null;
}>();

const slots = defineSlots<{
    default?: () => unknown;
}>();

const hasSlotOrMessage = computed(() => !!slots.default || !!props.message);

const progressSummary = computed(() => {
    if (!props.steps?.length) {
        return '';
    }
    const total = props.steps.length;
    const current = Math.min(Math.max(props.stepIndex ?? 0, 0), total);
    const displayed = current >= total ? total : current + 1;
    return `${displayed} / ${total}`;
});

const clampedProgress = computed(() => {
    const p = props.progress;
    if (typeof p !== 'number' || Number.isNaN(p)) {
        return 0;
    }
    return Math.max(0, Math.min(100, p));
});

function getStepState(index: number): 'done' | 'active' | 'pending' {
    const current = props.stepIndex ?? -1;
    if (index < current) {
        return 'done';
    }
    if (index === current) {
        return 'active';
    }
    return 'pending';
}

/* --- Animation SVG "Thinking Nine" --- */
const SVG_NS = 'http://www.w3.org/2000/svg';
const svgEl = ref<SVGSVGElement | null>(null);
const groupEl = ref<SVGGElement | null>(null);
const pathEl = ref<SVGPathElement | null>(null);
const particleNodes = shallowRef<SVGCircleElement[]>([]);
let rafId = 0;
let startedAt = 0;

const config = {
    rotate: true,
    particleCount: 43,
    trailSpan: 0.35,
    durationMs: 4700,
    rotationDurationMs: 17500,
    pulseDurationMs: 2500,
    strokeWidth: 5.5,
    baseRadius: 7,
    detailAmplitude: 3,
    petalCount: 9,
    curveScale: 3.9
};

function point(progress: number, detailScale: number) {
    const t = progress * Math.PI * 2;
    const petals = Math.round(config.petalCount);
    const x = config.baseRadius * Math.cos(t) - config.detailAmplitude * detailScale * Math.cos(petals * t);
    const y = config.baseRadius * Math.sin(t) - config.detailAmplitude * detailScale * Math.sin(petals * t);
    return { x: 50 + x * config.curveScale, y: 50 + y * config.curveScale };
}

function normalizeProgress(p: number) {
    return ((p % 1) + 1) % 1;
}

function getDetailScale(time: number) {
    const pulseProgress = (time % config.pulseDurationMs) / config.pulseDurationMs;
    const pulseAngle = pulseProgress * Math.PI * 2;
    return 0.52 + ((Math.sin(pulseAngle + 0.55) + 1) / 2) * 0.48;
}

function getRotation(time: number) {
    if (!config.rotate) {
        return 0;
    }
    return -((time % config.rotationDurationMs) / config.rotationDurationMs) * 360;
}

function buildPath(detailScale: number, steps = 480) {
    let d = '';
    for (let i = 0; i <= steps; i++) {
        const p = point(i / steps, detailScale);
        d += `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)} `;
    }
    return d;
}

function getParticle(index: number, progress: number, detailScale: number) {
    const tailOffset = index / (config.particleCount - 1);
    const p = point(normalizeProgress(progress - tailOffset * config.trailSpan), detailScale);
    const fade = Math.pow(1 - tailOffset, 0.56);
    return {
        x: p.x,
        y: p.y,
        radius: 0.9 + fade * 2.7,
        opacity: 0.04 + fade * 0.96
    };
}

function render(now: number) {
    if (!pathEl.value || !groupEl.value) {
        return;
    }
    const time = now - startedAt;
    const progress = (time % config.durationMs) / config.durationMs;
    const detailScale = getDetailScale(time);

    groupEl.value.setAttribute('transform', `rotate(${getRotation(time)} 50 50)`);
    pathEl.value.setAttribute('d', buildPath(detailScale));

    const nodes = particleNodes.value;
    for (let i = 0; i < nodes.length; i++) {
        const particle = getParticle(i, progress, detailScale);
        const node = nodes[i];
        node.setAttribute('cx', particle.x.toFixed(2));
        node.setAttribute('cy', particle.y.toFixed(2));
        node.setAttribute('r', particle.radius.toFixed(2));
        node.setAttribute('opacity', particle.opacity.toFixed(3));
    }

    rafId = requestAnimationFrame(render);
}

onMounted(() => {
    if (!groupEl.value) {
        return;
    }
    const created: SVGCircleElement[] = [];
    for (let i = 0; i < config.particleCount; i++) {
        const circle = document.createElementNS(SVG_NS, 'circle');
        circle.setAttribute('fill', 'currentColor');
        groupEl.value.appendChild(circle);
        created.push(circle);
    }
    particleNodes.value = created;
    startedAt = performance.now();
    rafId = requestAnimationFrame(render);
});

onBeforeUnmount(() => {
    if (rafId) {
        cancelAnimationFrame(rafId);
    }
    particleNodes.value.forEach((node) => node.parentNode?.removeChild(node));
    particleNodes.value = [];
});
</script>

<style scoped>
.ui-loader {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
}

.ui-loader--fullscreen {
    position: fixed;
    inset: 0;
    width: 100vw;
    /* `dvh` et non `vh` : sur iOS, `100vh` vaut la hauteur barres masquees, ce
       qui faisait deborder le voile sous la barre du navigateur. */
    height: 100dvh;
    /* Voile UNIFORME : gris foncé transparent (plus de bleu #40475b). */
    background: rgba(23, 26, 31, 0.6);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    z-index: 10000;
}

.ui-loader--opaque {
    /* Même voile gris foncé transparent que le plein écran (uniformisation). */
    background: rgba(23, 26, 31, 0.6) !important;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
}

.ui-loader:not(.ui-loader--fullscreen):not(.ui-loader--opaque) {
    color: var(--color-card-500, #1f2937);
}

.ui-loader__inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    max-width: 420px;
    padding: 24px;
}

.ui-loader__frame {
    width: min(36vmin, 220px);
    aspect-ratio: 1;
    display: grid;
    place-items: center;
}

.ui-loader__frame svg {
    width: 100%;
    height: 100%;
    overflow: visible;
}

.ui-loader__text {
    text-align: center;
}

.ui-loader__title {
    font-size: 1.05rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    margin: 0;
}

.ui-loader__progress {
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.55);
    margin: 0 0 8px 0;
    text-align: center;
}

.ui-loader:not(.ui-loader--fullscreen) .ui-loader__progress {
    color: rgba(31, 41, 55, 0.55);
}

.ui-loader__progressbar {
    position: relative;
    width: 100%;
    max-width: 280px;
    height: 6px;
    background: rgba(255, 255, 255, 0.12);
    border-radius: 999px;
    overflow: hidden;
}

.ui-loader__progressbar-fill {
    height: 100%;
    background: linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0.85) 100%);
    border-radius: 999px;
    transition: width 200ms ease-out;
    will-change: width;
}

.ui-loader__progressbar-label {
    position: absolute;
    top: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: rgba(255, 255, 255, 0.7);
    font-variant-numeric: tabular-nums;
}

.ui-loader:not(.ui-loader--fullscreen) .ui-loader__progressbar {
    background: rgba(31, 41, 55, 0.12);
}
.ui-loader:not(.ui-loader--fullscreen) .ui-loader__progressbar-fill {
    background: linear-gradient(90deg, var(--color-card-500, #1f2937) 0%, rgba(31, 41, 55, 0.85) 100%);
}
.ui-loader:not(.ui-loader--fullscreen) .ui-loader__progressbar-label {
    color: rgba(31, 41, 55, 0.7);
}

.ui-loader__steps {
    width: 100%;
    max-width: 320px;
    margin-top: 12px;
}

.ui-loader__steps-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0;
    margin: 0;
    list-style: none;
    text-align: left;
}

.ui-loader__step {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 0;
    transition: opacity 0.2s ease;
}

.ui-loader__step-icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    transition: all 0.3s ease;
}

.ui-loader__step-label {
    font-size: 0.88rem;
    line-height: 1.3;
}

/* done */
.ui-loader__step--done {
    color: rgba(255, 255, 255, 0.7);
}
.ui-loader__step--done .ui-loader__step-icon {
    background: rgba(74, 222, 128, 0.2);
    color: #4ade80;
}

/* active */
.ui-loader__step--active {
    color: #ffffff;
    font-weight: 500;
}
.ui-loader__step--active .ui-loader__step-icon {
    background: rgba(255, 255, 255, 0.18);
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.35);
}
.ui-loader__step-pulse {
    width: 8px;
    height: 8px;
    border-radius: 9999px;
    background: #ffffff;
    animation: ui-loader-pulse 1.1s ease-in-out infinite;
}
@keyframes ui-loader-pulse {
    0%,
    100% {
        opacity: 1;
        transform: scale(1);
    }
    50% {
        opacity: 0.55;
        transform: scale(0.65);
    }
}

/* pending */
.ui-loader__step--pending {
    color: rgba(255, 255, 255, 0.4);
}
.ui-loader__step--pending .ui-loader__step-icon {
    background: rgba(255, 255, 255, 0.08);
}
.ui-loader__step-dot {
    width: 6px;
    height: 6px;
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.4);
}

/* Inline (non-fullscreen) palette claire */
.ui-loader:not(.ui-loader--fullscreen):not(.ui-loader--opaque) .ui-loader__step--done {
    color: rgba(31, 41, 55, 0.7);
}
.ui-loader:not(.ui-loader--fullscreen):not(.ui-loader--opaque) .ui-loader__step--active {
    color: rgba(31, 41, 55, 1);
}
.ui-loader:not(.ui-loader--fullscreen):not(.ui-loader--opaque) .ui-loader__step--pending {
    color: rgba(31, 41, 55, 0.4);
}
.ui-loader:not(.ui-loader--fullscreen):not(.ui-loader--opaque) .ui-loader__step-pulse {
    background: var(--color-card-500, #1f2937);
}
.ui-loader:not(.ui-loader--fullscreen):not(.ui-loader--opaque) .ui-loader__step-dot {
    background: rgba(31, 41, 55, 0.4);
}
.ui-loader:not(.ui-loader--fullscreen):not(.ui-loader--opaque) .ui-loader__step--done .ui-loader__step-icon {
    background: rgba(34, 197, 94, 0.15);
    color: #16a34a;
}
</style>
